import { pxToRem } from '@back-office/core';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { DateRange, DateRangePicker } from '@material-ui/pickers';
import clsx from 'clsx';
import React, { FC, MouseEvent } from 'react';
import { getDatetimeText } from '../../utils';

const useStyles = makeStyles(
  (theme) => ({
    icon: {
      color: '#BDBDBD',
      width: pxToRem(14),
      height: pxToRem(14),
    },
    container: {
      border: '1px solid #BDBDBD',
      display: 'flex',
      padding: '5px 8px',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      color: '#BDBDBD',
      '& > .MuiButton-label': {
        gap: `${theme.spacing(1)}px`,
      },
      '&:hover': {
        border: `1px solid ${theme.palette.common.ceriseRed}`,
        color: theme.palette.common.ceriseRed,
        backgroundColor: 'unset',
        '& > span > svg': {
          color: theme.palette.common.ceriseRed,
        },
        '& > span > div > svg > path': {
          fill: theme.palette.common.ceriseRed,
        },
      },
    },
    end: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& > svg': {
        marginRight: '4px',
      },
    },
    fullWidth: {
      width: '100%',
    },
    date: {
      '& > .MuiButton-label': {
        color: 'black',
      },
    },
  }),
  { name: 'DatePicker' }
);

interface GcpDateRangeRecurringProps {
  openCalendar: boolean;
  date: DateRange<Date>;
  handleOnClick: () => void;
  handleOnClear: (event: MouseEvent<HTMLOrSVGElement>) => void;
  handleOnChange: (date: DateRange<Date>) => void;
  handleOnClose: () => void;
  fullWidth?: boolean;
  disableFuture?: boolean;
}

const GcpDateRangeRecurring: FC<GcpDateRangeRecurringProps> = (props) => {
  const classes = useStyles();
  const {
    openCalendar,
    date,
    handleOnClick,
    handleOnClear,
    handleOnChange,
    handleOnClose,
    fullWidth,
    disableFuture,
  } = props;

  return (
    <DateRangePicker
      disableFuture={disableFuture}
      open={openCalendar}
      renderInput={() => (
        <Button
          className={clsx(
            classes.container,
            {
              [classes.fullWidth]: fullWidth,
            },
            date[0] && date[1] && classes.date
          )}
          onClick={handleOnClick}
        >
          {date[0] ? (
            <>
              {getDatetimeText(date[0], 'numericDate')}
              <ArrowRightAltIcon className={classes.icon} />
            </>
          ) : (
            <>
              Start date
              <div className={classes.end}>
                <svg
                  fill="none"
                  height="16"
                  viewBox="0 0 17 16"
                  width="17"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.1422 9.31563L11.5797 6.06563C11.533 6.00629 11.4734 5.95832 11.4055 5.92531C11.3375 5.89229 11.263 5.87509 11.1875 5.875H10.175C10.0703 5.875 10.0125 5.99531 10.0766 6.07812L12.3313 8.9375H2.875C2.80625 8.9375 2.75 8.99375 2.75 9.0625V10C2.75 10.0687 2.80625 10.125 2.875 10.125H13.7484C14.1672 10.125 14.4 9.64375 14.1422 9.31563Z"
                    fill="#BDBDBD"
                  />
                </svg>
                End date
              </div>
            </>
          )}
          {date[1] && getDatetimeText(date[1], 'numericDate')}
          {date[0] && date[1] ? (
            <CloseIcon className={classes.icon} onClick={handleOnClear} />
          ) : (
            <CalendarTodayOutlinedIcon className={classes.icon} />
          )}
        </Button>
      )}
      value={date}
      onChange={handleOnChange}
      onClose={handleOnClose}
    />
  );
};

export default GcpDateRangeRecurring;
